import { Archive, EllipsisVertical, MessageSquareDot } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useState, type ComponentType, type ReactNode } from 'react';
import { ContextMenu, DropdownMenu, Flex, IconButton } from '@/components/common';
import type { RadixColors } from '@/components/common/RadixProps';
import { useCurrentUser } from '@/contexts';
import { useConversationActions } from '@/hooks';
import { isConversationUnread, type Conversation } from '@/services';
import { ArchiveConversationDialog } from './ArchiveConversationDialog';

interface MenuItemProps {
  color?: RadixColors;
  onSelect?: () => void;
  children: ReactNode;
}

interface ConversationMenuItemsProps {
  /** `ContextMenu.Item` (inbox row) or `DropdownMenu.Item` (chat header). */
  Item: ComponentType<MenuItemProps>;
  unread: boolean;
  onToggleUnread: () => void;
  onArchive: () => void;
}

/** The shared actions — extend this list to grow both menus. */
function ConversationMenuItems({
  Item,
  unread,
  onToggleUnread,
  onArchive,
}: ConversationMenuItemsProps) {
  const { t } = useTranslation('common');
  const { t: tChat } = useTranslation('chat');
  return (
    <>
      <Item onSelect={onToggleUnread}>
        <Flex align="center" gap="2">
          <MessageSquareDot aria-hidden size={16} />
          {unread ? t('actions.markRead') : t('actions.markUnread')}
        </Flex>
      </Item>
      <Item color="crimson" onSelect={onArchive}>
        <Flex align="center" gap="2">
          <Archive aria-hidden size={16} />
          {tChat('inbox.archive')}
        </Flex>
      </Item>
    </>
  );
}

function useArchiveFlow(conversation: Conversation, partnerName?: string, onArchived?: () => void) {
  const { t } = useTranslation('chat');
  const { user } = useCurrentUser();
  const { archiveConversation } = useConversationActions(user);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const confirmArchive = async () => {
    await archiveConversation(conversation.id);
    toast.success(t('inbox.archived'));
    onArchived?.();
  };

  const dialog = (
    <ArchiveConversationDialog
      open={archiveOpen}
      onOpenChange={setArchiveOpen}
      partnerName={partnerName}
      onConfirm={confirmArchive}
    />
  );

  return { openArchive: () => setArchiveOpen(true), dialog };
}

interface ConversationMenuProps {
  conversation: Conversation;
  partnerName?: string;
}

/** Long-press/right-click menu on an inbox row — wraps the row as trigger. */
export function ConversationContextMenu({
  conversation,
  partnerName,
  children,
}: ConversationMenuProps & { children: ReactNode }) {
  const { user } = useCurrentUser();
  const { markConversationRead, markConversationUnread } = useConversationActions(user);
  const { openArchive, dialog } = useArchiveFlow(conversation, partnerName);
  const unread = Boolean(user && isConversationUnread(conversation, user.uid));

  const toggleUnread = () => {
    if (!user) return;
    const write = unread
      ? markConversationRead(conversation.id)
      : markConversationUnread(conversation.id);
    void write.catch(err => console.error('Failed to update unread state:', err));
  };

  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger>{children}</ContextMenu.Trigger>
      <ContextMenu.Content>
        <ConversationMenuItems
          Item={ContextMenu.Item}
          unread={unread}
          onToggleUnread={toggleUnread}
          onArchive={openArchive}
        />
      </ContextMenu.Content>
      {dialog}
    </ContextMenu.Root>
  );
}

interface ConversationDropdownMenuProps extends ConversationMenuProps {
  /** Flags the thread unread from inside the chat; the menu exits to the inbox after. */
  onMarkUnread: (conversationId: string) => Promise<void>;
  /** Called after a successful archive (e.g. navigate back to the inbox). */
  onArchived?: () => void;
}

/** Kebab menu in the chat header. */
export function ConversationDropdownMenu({
  conversation,
  partnerName,
  onMarkUnread,
  onArchived,
}: ConversationDropdownMenuProps) {
  const { t } = useTranslation('chat');
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { markConversationRead } = useConversationActions(user);
  const { openArchive, dialog } = useArchiveFlow(conversation, partnerName, onArchived);
  const unread = Boolean(user && isConversationUnread(conversation, user.uid));

  const toggleUnread = () => {
    if (!user) return;
    if (unread) {
      void markConversationRead(conversation.id).catch(err =>
        console.error('Failed to update unread state:', err)
      );
      return;
    }
    void onMarkUnread(conversation.id).catch(err =>
      console.error('Failed to update unread state:', err)
    );
    navigate('/inbox');
  };

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <IconButton type="button" variant="ghost" size="2" aria-label={t('inbox.menu')}>
            <EllipsisVertical strokeWidth={2} aria-hidden size={20} />
          </IconButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
          <ConversationMenuItems
            Item={DropdownMenu.Item}
            unread={unread}
            onToggleUnread={toggleUnread}
            onArchive={openArchive}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Root>
      {dialog}
    </>
  );
}
